
import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_PCS,
  RECEIVE_ALL_PCS,
  RECEIVE_PC_METADATA,
  RECEIVE_PC_FAILURE,
  INCREMENT_PC_COUNT,
  RECEIVE_PC_LINES,
  RECEIVE_ALL_PC_LINES,
  RECEIVING_PC_LINES,
  RECEIVE_PC_LINES_METADATA,
  RECEIVE_PC_LINES_FAILURE,
  SAVING_PC_LINES,
  SAVING_PC_LINES_FAILURE,
  UPDATING_PC_LINES,
  UPDATING_PC_LINES_FAILURE,
  CLEAR_PC_STATE,
  CLEAR_STATE,
  CLEAR_PC_UPDATE_ERR_STATE,
  UPDATE_CURRENT_PC_PAGE,
  RESET_PC_META,
  DELETE_PC_LINES,
  CLEAR_PC_TRX_LINES,
  FILTER_PC_QUERY,
  CLEAR_FILTER_PC_QUERY
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import { fetchLinesNew, getTrxIds } from '@/store/modules/helpers/actionHelpers'
import {
  receiveData,
  increment,
  receiveMetadata,
  resetMetadata,
  receiveLinesMetadata,
  deleteLine,
  clearTrxLines
} from '@/store/modules/helpers/mutationHelpers'

const pcSchema = new schema.Entity('pcs')
const pcListSchema = new schema.Array(pcSchema)

const lineSchema = new schema.Entity('lines')
const lineListSchema = new schema.Array(lineSchema)

const state = {
  data: {},
  all: [],
  allLines: [],
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  receiveDataFailure: false,
  lines: {},
  lineCount: null,
  nextLineUrl: null,
  prevLineUrl: null,
  receivingLines: null,
  savingLines: null,
  updatingLines: null,
  deleting: false,
  filterInfo: {}
}

const actions = {
  /**
   * Fetches one or more PCs with the option to pass query string filter parameters
   * @param store
   * @param opts
   * @return {Promise<*>}
   */
  async fetchPcsWithQuery (store, opts) {
    let query
    let storeLines = false
    if (opts) {
      query = opts.hasOwnProperty('query') ? opts.query : opts
      storeLines = opts.hasOwnProperty('storeLines') ? opts.storeLines : false
    } else {
      query = {}
    }
    query.nest_lines = false
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.get(buildUrlWithQuery('pcs', query, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      this.commit(RESET_PC_META)
      const { results, next, previous, count } = resp
      const { entities, result } = normalize(results, pcListSchema)
      if (!(result && Array.isArray(result))) {
        throw new Error('No result set in response')
      }
      let mutationPayload = { data: entities.pcs }
      if (query && query.page && typeof query.page === 'number') {  // If query contains a page number
        mutationPayload.page = query.page                           // pass the page number to the mutation handler
      }
      this.commit(RECEIVE_PCS, mutationPayload)
      this.commit(RECEIVE_PC_METADATA, { next, previous, count, result: result.length })
      this.commit(UPDATE_CURRENT_PC_PAGE, query && query.page || 1)
      if (storeLines) {
        const lines = await fetchLinesNew.bind(this)({ trxIds: getTrxIds(entities.pcs), type: 'pc' })
        this.commit(RECEIVE_PC_LINES, { id: null, data: lines })
      }
      return { query, count, entities, results, next }
    } catch (e) {
      console.log(e)
      this.commit(RECEIVE_PC_FAILURE, handleXHRError(e))
    }
  },
  fetchAllPcLines (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_PC_LINES, true)
    return Vue.http.get(buildUrl('lines/pc?serialize=false', null, instanceUrl), { headers })
      .then(checkStatus)
      .then(response => {
        this.commit(RECEIVING_PC_LINES, false)
        if (!response || !Array.isArray(response.body)) return this.commit(RECEIVE_PC_LINES_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_ALL_PC_LINES, { data: response.body })
        return response.body
      })
      .catch(e => {
        this.commit(RECEIVING_PC_LINES, false)
        this.commit(RECEIVE_PC_LINES_FAILURE, e.statusText)
      })
  },
  fetchAllPcs (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.get(buildUrl('pcs?serialize=false&nest_lines=false', null, instanceUrl), { headers })
      .then(checkStatus)
      .then(response => {
        if (!response || !Array.isArray(response.body)) return this.commit(RECEIVE_PC_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_ALL_PCS, { data: response.body })
        return response.body
      })
      .catch(e => {
        this.commit(RECEIVE_PC_FAILURE, e.statusText)
      })
  },
  /**
   * Saves a PC transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async savePc (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.post(buildUrl('pcs', null, instanceUrl), payload, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!resp) {
        throw new Error(`Unable to save PC`)
      }
      const { entities, result } = normalize(resp, pcSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when saving PC`)
      }
      this.commit(RECEIVE_PCS, { data: entities.pcs })
      if (resp && resp.lines) {
        const e = normalize(resp.lines, lineListSchema)
        this.commit(RECEIVE_PC_LINES, { id: resp.id, data: e.entities.lines })
      }
      this.commit(INCREMENT_PC_COUNT, 1)
      return resp
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Updates a PC transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async updatePc (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.patch(buildUrl(`pcs/${id}`, null, instanceUrl), data, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!resp) {
        throw new Error(`Unable to update AP with id ${id}`)
      }
      const { entities, result } = normalize(resp, pcSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when updating PC with id ${id}`)
      }
      this.commit(RECEIVE_PCS, { data: entities.pcs })
      if (resp && resp.lines) {
        const e = normalize(resp.lines, lineListSchema)
        this.commit(RECEIVE_PC_LINES, { id: resp.id, data: e.entities.lines })
      }
      return resp
    } catch (e) {
      console.log(e)
      return handleXHRError(e)
    }
  },
  clearPcState (store, payload = null) {
    return this.commit(CLEAR_PC_STATE, payload)
  },
  clearPcTransactionLines (store, apId) {
    this.commit(CLEAR_PC_TRX_LINES, apId)
  },
  resetPcUpdateErrState () {
    return this.commit(CLEAR_PC_UPDATE_ERR_STATE)
  },
  resetPcMeta () {
    return this.commit(RESET_PC_META)
  },
  setPcFilterInfo (store, query) {
    this.commit(FILTER_PC_QUERY, query)
  },
  clearSetPcFilterInfo (store) {
    this.commit(CLEAR_FILTER_PC_QUERY)
  }
}

const getters = {
  getPcFilterInfo: (state) => {
    return state.filterInfo
  },
  getPcs: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getPcsCache: (state) => {
    return state.cache ? Object.values(state.cache).map(pc => Object.assign({}, pc)) : []
  },
  getPcById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAllPcs: (state) => {
    return state.all || []
  },
  getAllPcLines: (state) => {
    return state.allLines || []
  },
  getPcMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getPcLines: (state) => {
    return state.lines || {}
  },
  getPcLineById: (state) => (id) => {
    const { lines } = state
    for (let l in lines) {
      if (!lines.hasOwnProperty(l)) continue
      if (parseInt(lines[l].id) === parseInt(id)) return lines[l]
    }
    return {}
  },
  getPcLinesByTransactionId: (state) => (id) => {
    const { lines } = state
    if (!Object.keys(state.lines).length) return {}
    let ret = {}
    for (let l in state.lines) {
      if (lines[l].hasOwnProperty('transaction_id') && parseInt(lines[l].transaction_id) === parseInt(id)) {
        ret[l] = lines[l]
      }
    }
    return ret
  },
  getNextPcLineSet: (state) => {
    return state.nextLineUrl
  },
  getPrevPcLineSet: (state) => {
    return state.prevLineUrl
  },
  getCurrentPcPage: (state) => {
    if (state.currentPage) return state.currentPage
  }
}

const mutations = {
  [CLEAR_FILTER_PC_QUERY] (state) {
    state.filterInfo = {}
  },
  [FILTER_PC_QUERY] (state, query) {
    if (query.hasOwnProperty('keyword')) {
      state.filterInfo.keyword = query.keyword
    } if (query.hasOwnProperty('status__in')) {
      state.filterInfo.status__in = query.status__in
    } if (query.hasOwnProperty('batch')) {
      state.filterInfo.batch = query.batch
    } if (query.hasOwnProperty('sourcecode')) {
      state.filterInfo.sourcecode = query.sourcecode
    } if (query.hasOwnProperty('pageCount')) {
      state.filterInfo.pageCount = query.pageCount
    } if (query.hasOwnProperty('batch__bank__currency__in')) {
      state.filterInfo.batch__bank__currency__in = query.batch__bank__currency__in
    }
  },
  [RECEIVE_PCS] (state, results) {
    for (let d in results.data) {
      results.data[d]['transactionType'] = 'Petty Cash'
    }
    receiveData(state, results)
  },
  [RECEIVE_ALL_PCS] (state, results) {
    const { data } = results
    state.all = data
    return true
  },
  [RECEIVE_PC_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [RECEIVE_PC_LINES] (state, payload) {
    const { id, data } = payload
    if (!(data && (Array.isArray(data) || Object.keys(data).length))) {
      return state.lines
    }
    let tempLineState = { ...state.lines }
    let newData = {}
    if (Array.isArray(data) && data.every(line => !!line.id)) {
      data.forEach(line => (newData[line.id] = line))
    } else {
      newData = data
    }
    if (tempLineState && Object.keys(tempLineState).length) {
      tempLineState = { ...tempLineState, ...newData }
    } else {
      tempLineState = { ...newData }
    }
    if (id) {
      let tempState = { ...state.data }
      for (let d in newData) {
        if (tempState[id] && tempState[id].hasOwnProperty('lines') && Array.isArray(tempState[id].lines)) {
          tempState[id].lines = [...new Set([...tempState[id].lines, newData[d].id])]
        }
      }
      state.data = { ...tempState }
    }
    state.lines = { ...tempLineState }
  },
  [RECEIVE_ALL_PC_LINES] (state, results) {
    const { data } = results
    state.allLines = data
    return true
  },
  [RECEIVING_PC_LINES] (state, payload) {
    state.receivingLines = { status: payload, err: null }
  },
  [RECEIVE_PC_LINES_FAILURE] (state, payload) {
    state.receivingLines = { status: false, err: payload }
  },
  [SAVING_PC_LINES] (state, payload) {
    state.savingLines = { status: payload, err: null }
  },
  [SAVING_PC_LINES_FAILURE] (state, payload) {
    state.savingLines = { status: false, err: payload }
  },
  [UPDATING_PC_LINES] (state, payload) {
    state.updatingLines = { status: payload, err: null }
  },
  [UPDATING_PC_LINES_FAILURE] (state, payload) {
    state.updatingLines = { status: false, err: payload }
  },
  [INCREMENT_PC_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_PC_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [RECEIVE_PC_LINES_METADATA] (state, payload) {
    receiveLinesMetadata(state, payload)
  },
  [UPDATE_CURRENT_PC_PAGE] (state, payload) {
    state.currentPage = payload
  },
  [CLEAR_PC_STATE] (state, payload) {
    let newState = {
      data: Object.assign({}),
      lines: Object.assign({}),
      saving: false,
      receiveDataFailure: false,
      updating: null,
      updateFailure: null,
      deleting: false,
      receivingLines: null,
      savingLines: null,
      updatingLines: null
    }
    if (payload) {
      if (Array.isArray(payload) && payload.length) {
        payload.forEach(v => {
          if (newState.hasOwnProperty(v)) delete newState[v]
        })
      } else {
        newState['cache'] = Object.assign({})
      }
    }
    for (let key in newState) {
      state[key] = newState[key]
    }
  },
  [CLEAR_PC_TRX_LINES] (state, payload) {
    clearTrxLines(state, payload)
  },
  [RESET_PC_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    state.data = Object.assign({})
    state.cache = Object.assign({})
    state.lines = Object.assign({})
    state.all = []
    state.allLines = []
    state.count = null
    state.lineCount = null
    state.shown = null
    state.next = null
    state.previous = null
    state.currentPage = 1
    state.receiveDataFailure = false
    state.nextLineUrl = null
    state.prevLineUrl = null
    state.receivingLines = null
    state.savingLines = null
    state.updatingLines = null
    state.deleting = false
  },
  [CLEAR_PC_UPDATE_ERR_STATE] (state) {
    state.updateFailure = null
  },
  [DELETE_PC_LINES] (state, payload) {
    deleteLine(state, payload)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/pettycash.js